import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AudioToText = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/audio-to-text',
			name: 'Audio to Text Converter: Turn MP3 Recordings into Written Transcripts',
			description:
				"Turn podcasts, calls, and lectures into written words with Lift's AI audio transcription, then copy or download the result. Try Lift today!",
			url: 'https://liftapp.ai/audio-to-text',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/audio-to-text#product' },
				{ '@id': 'https://liftapp.ai/audio-to-text#howto' },
				{ '@id': 'https://liftapp.ai/audio-to-text#benefits' },
				{ '@id': 'https://liftapp.ai/audio-to-text#faq' },
				{ '@id': 'https://liftapp.ai/audio-to-text#why-lift' },
				{ '@id': 'https://liftapp.ai/audio-to-text#explore-tools' },
				{ '@id': 'https://liftapp.ai/audio-to-text#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/audio-to-text#product',
			name: 'Transcribe Audio to Text Online',
			description:
				"Upload a recording, let AI recognize what's said, and get a readable transcript in a few clicks. Try Lift today!",
			url: 'https://liftapp.ai/audio-to-text',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/audio-to-text#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your audio file',
					text: 'Add an MP3 or another common format straight from your device, from short clips to full episodes.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Set the spoken language',
					text: 'Leave it on Auto for detection, or pick one manually and toggle audio cues on or off.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Let AI transcribe the recording',
					text: 'Tap Generate, and Lift writes out every line, keeping it in My Transcriptions for later.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Summarize or download the result',
					text: 'Copy the text, get a quick summary, or save everything as a TXT or PDF file.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/audio-to-text#benefits',
			name: 'Who can benefit from the Audio to Text Converter?',
			description:
				"Lift's Audio to Text Converter helps podcasters, producers, learners, sales teams, and deaf or hard-of-hearing users turn recordings into readable text.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Podcasters — Convert episode audio into text for show notes, blog posts, and searchable archives.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Radio and audiobook producers — Get a written version of recorded shows and chapters to check against the script.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Language learners — Read along with recorded lessons and podcasts to catch words you missed by ear.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Sales managers — Transcribe recorded client calls and summarize the key points before updating the CRM.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Deaf and hard-of-hearing users — Read the text of any audio clip, with background sounds marked when they matter.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/audio-to-text/benefits.webp',
				name: 'Audio to Text Converter by Lift',
				description:
					'Phone on a study desk showing an audio transcription summary, ready to download as TXT or PDF',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/audio-to-text#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Audio to Text Converter?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The Lift Audio to Text Converter uses AI to transcribe speech from audio files into written text. It works with podcasts, calls, lectures, and other recordings.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift Audio to Text Converter work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload an audio file, keep the language on Auto or set it, and tap Generate. Open the transcript to copy it, summarize it, clean it up, or download it.',
					},
				},
				{
					'@type': 'Question',
					name: 'Which audio files can I convert to text?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'You can upload MP3, WAV, M4A, and other common audio formats. Lift handles long recordings, so a full episode or lecture can be transcribed in one go.',
					},
				},
				{
					'@type': 'Question',
					name: 'How can I save the transcribed text?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Use Copy to send everything to your clipboard, or Download to get a TXT or PDF file. If you also created a summary, export it on its own or together with the transcription.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my audio files to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your audio securely and uses it only to generate your transcript.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/audio-to-text#why-lift',
			name: 'Why choose Lift to convert audio to text',
			description:
				"Lift's Audio to Text Converter pairs automatic language detection and audio cues with saved transcripts in one affordable tool on iOS, Android, and Web.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/audio-to-text/whyLift.webp',
				name: 'Why use Lift to convert audio to text',
				description:
					"Woman on a sofa uploading an MP3 to Lift's online audio to text tool on her tablet",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/audio-to-text#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/audio-to-text#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Audio to Text',
					item: 'https://liftapp.ai/audio-to-text',
				},
			],
		},
	],
};
